import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Users } from 'lucide-react'
import { format } from 'date-fns'
import { logger } from '@magnetar/utils'

interface PatientCurrentFlowProps {
  clinicId?: number
  broadcastDeviceId?: number
}

interface PatientFlowData {
  clinicId: number
  currentFlow: number
  timestamp: string
}

export function PatientCurrentFlow({ clinicId, broadcastDeviceId }: PatientCurrentFlowProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [currentFlow, setCurrentFlow] = useState<PatientFlowData | null>(null)

  useEffect(() => {
    // 檢查是否有有效的 ID
    if (!clinicId && !broadcastDeviceId) return

    // 根據提供的 ID 類型決定使用哪個 WebSocket 端點
    let wsUrl = ''
    if (broadcastDeviceId) {
      wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${
        import.meta.env.VITE_WS_URL
      }/ws/device-patient-flow?broadcast_device_id=${broadcastDeviceId}`
    } else if (clinicId) {
      wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${
        import.meta.env.VITE_WS_URL
      }/ws/patient-flow?clinic_id=${clinicId}`
    }

    const newSocket = new WebSocket(wsUrl)

    newSocket.onopen = () => {
      if (broadcastDeviceId) {
        logger.log('WebSocket connected for broadcast device:', broadcastDeviceId)
      } else if (clinicId) {
        logger.log('WebSocket connected for clinic:', clinicId)
      }
    }

    newSocket.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data)

        if (response.success) {
          // Handle regular flow updates
          if (response.data.currentFlow !== undefined) {
            setCurrentFlow({
              clinicId: response.data.clinicId || 0,
              currentFlow: response.data.currentFlow,
              timestamp: response.data.timestamp,
            })
            setIsLoading(false)
          }

          // Handle specific patient_flow_update events
          if (response.data.type === 'patient_flow_update') {
            setCurrentFlow({
              clinicId: response.data.data.clinicId || 0,
              currentFlow: response.data.data.currentFlow,
              timestamp: response.data.data.timestamp,
            })
            setIsLoading(false)
          }
        } else {
          logger.error('WebSocket error:', response.error)
        }
      } catch (error) {
        logger.error('Error parsing WebSocket message:', error)
      }
    }

    newSocket.onerror = (error) => {
      logger.error('WebSocket error:', error)
    }

    newSocket.onclose = () => {
      logger.log('WebSocket connection closed')
    }

    // No need to store the socket in state

    // Cleanup function to close the WebSocket when the component unmounts
    return () => {
      if (newSocket.readyState === WebSocket.OPEN) {
        newSocket.close()
      }
    }
  }, [clinicId, broadcastDeviceId])

  return (
    <Card className="md:col-span-1">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">當前人流</CardTitle>
        <CardDescription>即時診所人流數據</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center justify-center bg-primary/10 rounded-full h-24 w-24 mb-2">
              <Users className="h-10 w-10 text-primary" />
            </div>
            <div className="text-4xl font-bold">{currentFlow?.currentFlow || 0}</div>
            <div className="text-xs text-muted-foreground mt-1">
              更新於{' '}
              {currentFlow?.timestamp
                ? format(new Date(currentFlow.timestamp), 'yyyy-MM-dd HH:mm:ss')
                : ''}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
